import { z } from '@repo/schema'
import type { FilterModel, FiltersState } from '@repo/table/filters'

// Invoices overdue and All bills overdue became overdue choices on the due dates they counted
// from (CK-3053). Shared links and Saved Views from before still hold them. They also sent an
// awaiting-payment status, which the due date already implies: the API only sets a next payment
// due date while a document is still outstanding.
const DUE_DATE_OF_OVERDUE = new Map([
  ['bills_overdue', 'bills_next_payment_due_date'],
  ['invoices_overdue', 'invoices_next_payment_due_date'],
])

// "any" is 0; else a whole number of days up to ten years, the furthest a date filter offset goes.
// Written out rather than imported: the Saved View page runs this on the server, and the
// `@repo/table/filters` entry pulls in client components.
const OverdueDaysSchema = z.union([
  z.literal('any').transform(() => 0),
  z.string().regex(/^\d+$/).transform(Number).pipe(z.number().max(3650)),
])

/**
 * Turns each legacy overdue filter into "is before" its due date, a day count back from today,
 * which is the same request. A due date the user already filters by keeps its own filter, as a
 * column holds one filter. Filters without a legacy one come back as the same array.
 */
export const migrateOverdueFilters = (filters: FiltersState): FiltersState => {
  if (!filters.some(filter => DUE_DATE_OF_OVERDUE.has(filter.columnId))) return filters

  return filters.flatMap((filter): FiltersState => {
    const columnId = DUE_DATE_OF_OVERDUE.get(filter.columnId)
    if (!columnId) return [filter]

    const days = OverdueDaysSchema.safeParse(filter.values[0]).data
    if (days === undefined || filters.some(other => other.columnId === columnId)) return []

    const overdue: FilterModel<'date'> = {
      columnId,
      operator: 'is before',
      type: 'date',
      values: [{ days: -days || 0 }],
    }
    return [overdue]
  })
}
